<template>
    <q-virtual-scroll
        ref="virtualScroll"
        type="table"
        :items="phases"
        :virtual-scroll-item-size="70"
        :virtual-scroll-sticky-size-start="48"
        :virtual-scroll-sticky-size-end="32"
        class="no-shadow phases-table"
        square
    >
        <!-- Header -->
        <template v-slot:before>
            <q-tr>
                <q-th
                    v-for="col in table.columns"
                    :key="col.label"
                    class="phases-table-header"
                >
                    <div :align="col.align">
                        {{ $t(col.label) }}
                        <span v-if="col.addSpacer" class="icon-spacer"></span>
                    </div>
                </q-th>
                <!-- Media Column Header -->
                <q-th
                    v-if="isMediaUploadEnabled"
                    class="phases-table-header phases-table-media-column"
                >
                </q-th>
                <!-- Proximity Sensor Column Header -->
                <q-th
                    v-if="hasProximitySensor"
                    class="phases-table-header phases-table-proximity-column"
                >
                </q-th>
                <!-- Actions Column Header -->
                <q-th class="phases-table-header phases-table-actions-column">
                </q-th>
                <q-th class="phases-table-header" colspan="100%"></q-th>
            </q-tr>
        </template>
        <!-- Rows -->
        <template v-slot="{ item: phase }">
            <q-tr :class="dynamicRowClass(phase)" :key="phase.id">
                <!-- Phase Status -->
                <q-td
                    @click="phase.selected = !phase.selected"
                    class="no-padding-cell phases-table-status-column"
                >
                    <div
                        class="flex items-center no-wrap phases-table-status"
                        :class="getPhaseStatusBgClass(phase.phase)"
                    >
                        <!-- Merge/Bulk Update Mode -->
                        <div v-if="isMergeEnabled || isBulkUpdateEnabled">
                            <q-checkbox
                                class="select-phase-checkbox"
                                v-model="phase.selected"
                                color="accent"
                            />
                        </div>
                        <div class="phases-table-status-cell">
                            <q-icon
                                :color="
                                    ASSET_STATUS_TO_STYLE[phase.phase]['color']
                                "
                                :name="
                                    ASSET_STATUS_TO_STYLE[phase.phase]['icon']
                                "
                                class="q-mx-none q-mb-sm"
                                size="sm"
                                square
                                outline
                            >
                                <q-tooltip>
                                    {{
                                        ASSET_STATUS_TO_STYLE[phase.phase][
                                            'label'
                                        ]
                                    }}
                                </q-tooltip>
                            </q-icon>
                        </div>
                        <div v-if="phase.phase === 'running'">
                            {{ $t('Running') }}
                        </div>
                        <div v-else-if="phase.cause">
                            {{ phase.cause_name }}
                        </div>
                        <div v-else>
                            <div
                                v-if="
                                    phase.phase === 'in_downtime' ||
                                    phase.phase === 'changeover'
                                "
                            >
                                <!-- If the phase is major or stopped, show list of causes -->
                                <div v-if="phase.is_major">
                                    <!-- Show list of causes -->
                                    <missing-cause-label
                                        v-model="causeSelectProps.selection"
                                        :phaseId="phase.id"
                                        :phaseEnd="phase.end"
                                        :phaseUUID="phase.uuid"
                                        @refreshList="$emit('refreshList')"
                                        @clearSelection="
                                            causeSelectProps.selection = null
                                        "
                                        :baseURL="causeSelectProps.url"
                                        :queryParams="
                                            getCauseQueryParams(phase.phase)
                                        "
                                        :label="causeSelectProps.label"
                                        :dialogTitle="
                                            causeSelectProps.dialogTitle
                                        "
                                        :searchLabel="
                                            causeSelectProps.searchLabel
                                        "
                                        :no-data-label="
                                            getNoShutdownCausesMessage(
                                                phase.phase
                                            )
                                        "
                                        isOutlined
                                    >
                                        <template v-slot:item="data">
                                            <cause-card
                                                @select="
                                                    select(
                                                        data.props.row,
                                                        causeSelectProps
                                                    )
                                                "
                                                :name="data.props.row.name"
                                                :lossType="
                                                    data.props.row.type_of_loss
                                                "
                                                :categoryName="
                                                    data.props.row.category_name
                                                "
                                                :planned="
                                                    data.props.row.planned
                                                "
                                                :description="
                                                    data.props.row.description
                                                "
                                                :targetDowntime="
                                                    data.props.row
                                                        .target_down_time
                                                "
                                                :isSelected="
                                                    causeSelectProps.selection &&
                                                    causeSelectProps.selection
                                                        .value ===
                                                        data.props.row.id
                                                "
                                                :isSelectable="true"
                                                :equipmentFailure="
                                                    data.props.row
                                                        .equipment_failure
                                                "
                                            />
                                        </template>
                                    </missing-cause-label>
                                </div>
                                <!-- If the phase is in downtime and it is not major, show minor downtime -->
                                <div
                                    v-else-if="
                                        phase.phase === 'in_downtime' &&
                                        !phase.is_major
                                    "
                                >
                                    {{ $t('Minor Downtime') }}
                                </div>
                            </div>
                            <!-- If there is no cause and the phase is stopped, show shutdown -->
                            <div
                                v-else-if="
                                    !phase.cause && phase.phase === 'stopped'
                                "
                            >
                                {{ $t('Shutdown') }}
                            </div>
                        </div>
                    </div>
                </q-td>

                <!-- Start/End -->
                <q-td
                    @click="phase.selected = !phase.selected"
                    class="phases-table-started-column"
                >
                    {{
                        getMonthDayTime(
                            phase.start,
                            userLocale,
                            selectedTimezone
                        )
                    }}
                </q-td>

                <!-- Duration -->
                <q-td
                    @click="phase.selected = !phase.selected"
                    align="right"
                    class="phases-table-duration-column"
                >
                    {{ convertDurationToDHMS(phase.duration_s) }}
                    <q-icon
                        v-if="phase.phase !== 'running'"
                        name="fa-regular fa-info-circle"
                        class="q-mr-xs"
                        color="grey"
                        size="xs"
                    >
                        <q-tooltip>
                            <div>
                                {{ $t('No Product Duration') }}
                            </div>
                            <div>
                                {{
                                    convertDurationToDHMS(
                                        calcNoProductDuration(phase)
                                    )
                                }}
                            </div>
                        </q-tooltip>
                    </q-icon>
                    <div v-else class="icon-spacer"></div>
                </q-td>

                <!-- Product Count -->
                <q-td
                    @click="phase.selected = !phase.selected"
                    align="right"
                    class="phases-table-count-column"
                >
                    {{
                        formatProductionValue(
                            phase.product_count,
                            userLocale
                        ) || '-'
                    }}
                </q-td>

                <!-- Product Quantity -->
                <q-td
                    @click="phase.selected = !phase.selected"
                    align="right"
                    class="phases-table-quantity-column"
                >
                    {{ formatRowProductQuantity(phase) || '-' }}
                </q-td>

                <!-- Description -->
                <q-td
                    class="phases-table-description-column"
                    @click="phase.selected = !phase.selected"
                >
                    <div
                        v-if="phase.description.length <= descriptionMaxLength"
                    >
                        {{ phase.description }}
                    </div>
                    <div v-else>
                        {{
                            phase.description.substring(
                                0,
                                descriptionMaxLength
                            )
                        }}...
                        <span
                            @click="$emit('showDescription', phase.description)"
                            class="text-accent cursor-pointer"
                        >
                            {{ $t('read more') }}
                        </span>
                    </div>
                </q-td>

                <!-- Media -->
                <q-td
                    v-if="isMediaUploadEnabled"
                    @click="phase.selected = !phase.selected"
                    class="phases-table-media-column"
                >
                    <media-gallery-button
                        :mediaIds="phase.media"
                        :phaseId="phase.id"
                    />
                </q-td>

                <!-- Proximity Sensor -->
                <q-td
                    v-if="hasProximitySensor"
                    @click="phase.selected = !phase.selected"
                    class="phases-table-proximity-column"
                >
                    <q-icon
                        v-if="phase.proximity_reading !== null"
                        :name="
                            phase.proximity_reading
                                ? 'far fa-sensor-on'
                                : 'far fa-sensor'
                        "
                        class="q-mr-xs"
                        size="sm"
                        :color="
                            phase.proximity_reading === 1
                                ? 'accent'
                                : 'secondary'
                        "
                    >
                        <q-tooltip anchor="bottom middle" self="top middle">
                            {{
                                $t(
                                    PROXIMITY_SENSOR_LABELS[
                                        phase.proximity_reading
                                    ]?.label
                                )
                            }}
                        </q-tooltip>
                    </q-icon>
                </q-td>

                <!-- Actions -->
                <q-td
                    @click="phase.selected = !phase.selected"
                    class="text-right phases-table-actions-column"
                >
                    <q-btn
                        id="edit-phase-button"
                        @click="edit(phase)"
                        text-color="accent"
                        icon="fas fa-edit"
                        color="white"
                        class="phases-table-button"
                        outline
                        :disable="
                            isBulkUpdateEnabled ||
                            isMergeEnabled ||
                            !$userCan('edit', 'phases') ||
                            !isPhaseEditable(phase.end)
                        "
                    >
                        <q-tooltip
                            v-if="getUneditableErrorMessage(phase.end)"
                            anchor="bottom middle"
                            self="top middle"
                        >
                            {{ getUneditableErrorMessage(phase.end) }}
                        </q-tooltip>
                    </q-btn>
                    <q-btn
                        id="split-button"
                        @click="showSplitDialog(phase)"
                        :disable="
                            $userCannot('split', 'phases') ||
                            !isSplittablePhase(phase) ||
                            isMergeEnabled ||
                            isBulkUpdateEnabled
                        "
                        color="white"
                        :text-color="
                            $userCan('split', 'phases') &&
                            isSplittablePhase(phase)
                                ? 'warning'
                                : 'grey'
                        "
                        class="phases-table-button q-ml-sm"
                        outline
                    >
                        <q-icon style="text-align: center" name="call_split" />
                        <q-tooltip anchor="bottom middle" self="top middle">
                            <span v-if="!isSplittablePhase(phase)">
                                {{
                                    $t(
                                        'This phase is too short to be split. A phase must be at least be 3 seconds long to split.'
                                    )
                                }}
                            </span>
                            <span v-else>
                                {{ $t('Split phase') }}
                            </span>
                        </q-tooltip>
                    </q-btn>
                </q-td>
            </q-tr>
        </template>
    </q-virtual-scroll>
</template>
<script>
import {
    isPhaseEditable,
    getUneditableErrorMessage
} from '@utils/phaseChecker.js';
import { formatProductionValue } from '@utils/numberFormatters.js';
import { convertDurationToDHMS } from '@utils/time.js';
import { getMonthDayTime } from '@models/timeformats.js';
import { convertToUnitSystem } from '@models/products.js';
import {
    ASSET_STATUS_TO_STYLE,
    MIN_SECONDS_FOR_PHASE_SPLIT
} from '@models/assets.js';
import { PROXIMITY_SENSOR_LABELS } from '@models/devices.js';
import MissingCauseLabel from '@components/MissingCauseLabel.vue';
import CauseCard from '@components/CauseCard.vue';
import MediaGalleryButton from '@components/MediaGalleryButton.vue';
import { isFeatureEnabled } from '@utils/featureFlags.js';
import { calcNoProductDuration } from '@utils/events.js';
import { scrollToPhase } from '@utils/phaseScrolling.js';
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { date } from 'quasar';

export default {
    name: 'PhasesTable',
    emits: ['edit', 'split', 'refreshList', 'showDescription'],
    components: {
        MissingCauseLabel,
        CauseCard,
        MediaGalleryButton
    },
    props: {
        phases: {
            type: Array,
            required: true
        },
        isMergeEnabled: {
            type: Boolean,
            required: true
        },
        isBulkUpdateEnabled: {
            type: Boolean,
            required: true
        },
        selectedTimezone: {
            type: String,
            required: true
        },
        facilityUnitSystem: {
            type: String,
            required: true
        },
        assetId: {
            type: Number,
            required: true
        },
        hasProximitySensor: {
            type: Boolean,
            required: true
        },
        descriptionMaxLength: {
            type: Number,
            required: false,
            default: 50
        },
        highlightedPhaseId: {
            type: [Number, String],
            required: false,
            default: null
        }
    },
    async created() {
        // initialize table columns
        this.table.columns = this.initTableColumns();
    },
    watch: {
        /**
         * Watch for changes in the MEDIA_UPLOAD feature flag and
         * rebuild the table columns accordingly so the Media column
         * is shown/hidden reactively (e.g. after page refresh when the
         * flag value becomes available).
         *
         * @param {boolean} newVal - Current value of the feature flag.
         */
        isMediaUploadEnabled: {
            immediate: true,
            handler(newVal) {
                // Re-initialize columns every time the flag changes.
                // Using a fresh array from initTableColumns preserves
                // the original column order and any later additions.
                this.table.columns = this.initTableColumns();
            }
        },
        /**
         * Watch for changes in highlightedPhaseId and scroll to the phase.
         * @param {number|string|null} newPhaseId - The new highlighted phase ID
         */
        highlightedPhaseId: {
            handler(newPhaseId) {
                if (newPhaseId !== null) {
                    // Add a delay to ensure DOM is fully updated
                    // This is especially important when product runs have been expanded
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.scrollToPhaseInTable(newPhaseId);
                        }, 300);
                    });
                }
            }
        }
    },
    data() {
        return {
            // models
            ASSET_STATUS_TO_STYLE,
            PROXIMITY_SENSOR_LABELS,
            MIN_SECONDS_FOR_PHASE_SPLIT,
            table: {
                data: [],
                columns: [],
                pagination: {
                    sortBy: 'start',
                    descending: true,
                    page: 1,
                    rowsPerPage: 1000,
                    rowsNumber: null
                }
            },
            // selectors for missing-cause-label missing cause
            causeSelectProps: {
                url: `assets/${this.assetId}/causes/`,
                label: this.$t('Cause'),
                dialogTitle: this.$t('Select a cause'),
                searchLabel: this.$t('Search cause'),
                selection: null
            }
        };
    },
    computed: {
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Verify the state of the MEDIA_UPLOAD feature flag
         * @return {Boolean}
         */
        isMediaUploadEnabled() {
            return this.isFeatureEnabled('MEDIA_UPLOAD');
        }
    },
    methods: {
        isPhaseEditable,
        getUneditableErrorMessage,
        formatProductionValue,
        convertDurationToDHMS,
        isFeatureEnabled,
        calcNoProductDuration,
        getMonthDayTime,
        /**
         *  Initialize the table columns.
         */
        initTableColumns() {
            return [
                {
                    label: 'Status',
                    align: 'left'
                },
                {
                    label: 'Started at',
                    align: 'left'
                },
                {
                    field: 'duration_s',
                    label: 'Duration',
                    align: 'right',
                    addSpacer: true
                },
                {
                    field: 'product_count',
                    label: 'Product Count',
                    align: 'right'
                },
                {
                    field: 'product_quantity',
                    label: 'Quantity',
                    align: 'right'
                }
            ];
        },
        /**
         * Format the product quantity row.
         * @param {Object} row
         * @return {String} - formatted product quantity. Null if no product quantity.
         */
        formatRowProductQuantity(row) {
            if (row.product_quantity === null) {
                return null;
            }

            const { value: converted_value, unit: converted_unit } =
                convertToUnitSystem(
                    parseFloat(row.product_quantity),
                    row.unit_measure,
                    this.facilityUnitSystem
                );

            const decimalPlaces = 0;

            const formattedValue = this.formatProductionValue(
                converted_value,
                this.userLocale,
                decimalPlaces
            );

            return this.$t('{value} {unit}', {
                value: formattedValue,
                unit: converted_unit
            });
        },
        /**
         * Get query parameters for cause select.
         * In case of stopped phase, only show shutdown causes.
         * @return {Object}
         */
        getCauseQueryParams(status) {
            const urlParams = { ordering: 'name' };
            if (status === 'stopped') {
                urlParams['type_of_loss'] = 'shutdown';
            }
            return urlParams;
        },
        /**
         * Show a custom message if the selected phase is a stopped phase
         * and no shutdown causes are available.
         * @return {String}
         */
        getNoShutdownCausesMessage(status) {
            if (status === 'stopped') {
                return this.$t(
                    'No planned shutdown cause is available for this line.'
                );
            }
            return '';
        },
        /**
         * Show pointer cursor if the row is in merge or bulk update mode.
         * Add a class on the row corresponding phase selected in the graph.
         * @param {Object} phase - The phase object
         * @return {String}
         */
        dynamicRowClass(phase) {
            let classes = '';
            if (this.isMergeEnabled || this.isBulkUpdateEnabled) {
                classes += 'cursor-pointer ';
            }
            if (
                this.highlightedPhaseId &&
                phase &&
                phase.id === this.highlightedPhaseId
            ) {
                classes += 'highlighted-phase ';
            }
            return classes;
        },
        /**
         * Get the background color class for the phase status.
         * @param {String} phase - The phase status
         * @return {String} - The background color class
         */
        getPhaseStatusBgClass(phase) {
            return ASSET_STATUS_TO_STYLE[phase]['backgroundClass'];
        },
        /**
         * Determine if a phase is splittable.
         * @params {Object}
         * @return {Boolean}
         */
        isSplittablePhase(row) {
            // check if phase is more than MIN_SECONDS_FOR_PHASE_SPLIT seconds
            return row.duration_s > this.MIN_SECONDS_FOR_PHASE_SPLIT;
        },
        /**
         * Emit the edit event.
         * @param {Object} row - The row to edit
         */
        edit(row) {
            this.$emit('edit', row);
        },
        /**
         * Emit the split event.
         * @param {Object} row - The row to split
         */
        showSplitDialog(row) {
            this.$emit('split', row);
        },
        /**
         * Select/ deselect cause and products in table-select component
         * @param {Object} row - Event from table row.
         * @param {Object} props - Table selection props (MissingCauseLabel)
         */
        select(row, props) {
            if (props.selection && props.selection.value === row.id) {
                props.selection = null;
            } else {
                props.selection = {
                    label: row.name,
                    value: row.id
                };
            }
        },
        /**
         * Scroll to a specific phase in the virtual scroll table.
         * @param {number|string} phaseId - The phase ID to scroll to
         */
        scrollToPhaseInTable(phaseId) {
            scrollToPhase({
                phaseId,
                phases: this.phases,
                $refs: this.$refs,

                nextTickFn: this.$nextTick,
                scrollType: 'table',
                tableConfig: {
                    itemHeight: 70, // height from CSS
                    headerHeight: 48, // approximate header height
                    virtualScrollRef: 'virtualScroll'
                },
                offset: 100
            });
        }
    }
};
</script>
<style lang="scss" scoped>
// Set the font size of the table
:deep(.q-table th),
:deep(.q-table td) {
    font-size: 13.5px;
}

// Set the height of the table rows
td {
    height: 70px !important;
}

.phases-table {
    margin-inline: 40px;
    border: 1px solid $lighter-grey;
    border-top: none;
    background-color: $background-grey;
    table-layout: fixed;
}

.phases-table-header {
    font-weight: normal;
    color: $secondary;
    border-bottom: 1px solid $lighter-grey !important;
}

// Fixed column widths for consistent alignment across multiple tables
.phases-table-status-column {
    width: 300px;
    min-width: 300px;
    max-width: 300px;
}

.phases-table-started-column {
    width: 150px;
    min-width: 150px;
    max-width: 150px;
}

.phases-table-duration-column {
    width: 120px;
    min-width: 120px;
    max-width: 120px;
}

.phases-table-count-column {
    width: 120px;
    min-width: 120px;
    max-width: 120px;
}

.phases-table-quantity-column {
    width: 120px;
    min-width: 120px;
    max-width: 120px;
}

.phases-table-description-column {
    width: 200px;
    min-width: 200px;
    max-width: 200px;
    white-space: initial;
    word-wrap: break-word;
}

.phases-table-media-column {
    width: 80px;
    min-width: 80px;
    max-width: 80px;
    text-align: center;
}

.phases-table-proximity-column {
    width: 80px;
    min-width: 80px;
    max-width: 80px;
    text-align: center;
}

.phases-table-actions-column {
    width: 140px;
    min-width: 140px;
    max-width: 140px;
}

.phases-table-button {
    padding: 6px;
    font-size: 12px;
}

.phases-table-status {
    height: 100%;
    padding-left: 20px !important;
}

.phases-table-status-cell {
    margin-right: 10px;
    margin-top: 5px;
}

.no-padding-cell {
    padding: 0 !important;
}

.icon-spacer {
    display: inline-block;
    width: 22px;
    height: 18px;
}

.highlighted-phase td {
    border-top: 4px solid $accent !important;
    border-bottom: 4px solid $accent !important;
    position: relative;
}

.highlighted-phase td:first-child {
    border-left: 4px solid $accent !important;
}

.highlighted-phase td:last-child {
    border-right: 4px solid $accent !important;
}
</style>
